import { useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import {
    Plus, Edit, Trash2, Search, Download, Truck, ChevronLeft, ChevronRight, ArrowUpDown,
} from 'lucide-react';

const AVATAR_COLORS = ['#f59e0b', '#8b5cf6', '#10b981', '#3b82f6', '#ec4899', '#14b8a6', '#f97316', '#6366f1'];

const initialsOf = (name) => (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

const avatarColor = (name) => {
    const text = name || '';
    let hash = 0;
    for (let i = 0; i < text.length; i += 1) hash = (hash + text.charCodeAt(i)) % AVATAR_COLORS.length;
    return AVATAR_COLORS[hash];
};

const emptyForm = { nombre: '', telefono: '', email: '', direccion: '' };

export default function ProveedoresPage() {
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [formData, setFormData] = useState(emptyForm);
    const [editingId, setEditingId] = useState(null);
    const [query, setQuery] = useState('');
    const [sortBy, setSortBy] = useState('nombre');
    const [selected, setSelected] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const fetchData = async () => {
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${API_URL}/api/providers`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) setProviders(await res.json());
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Carga inicial del catálogo.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        fetchData();
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        let rows = providers.filter((provider) => {
            if (!q) return true;
            return [provider.nombre, provider.email, provider.telefono, provider.direccion, provider.proveedor_id]
                .join(' ')
                .toLowerCase()
                .includes(q);
        });
        rows = [...rows].sort((a, b) => {
            if (sortBy === 'telefono') return (a.telefono || '').localeCompare(b.telefono || '', 'es');
            if (sortBy === 'recientes') {
                const ta = new Date(a.updated_at || a.created_at || 0).getTime();
                const tb = new Date(b.updated_at || b.created_at || 0).getTime();
                return tb - ta || (a.nombre || '').localeCompare(b.nombre || '', 'es');
            }
            return (a.nombre || '').localeCompare(b.nombre || '', 'es');
        });
        return rows;
    }, [providers, query, sortBy]);

    const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
    const safePage = Math.min(page, pageCount);
    const pageRows = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
    const from = filtered.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
    const to = Math.min(safePage * pageSize, filtered.length);
    const pageIds = pageRows.map((provider) => provider.proveedor_id);
    const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.includes(id));

    const toggleAllPage = () => {
        setSelected((prev) => (
            allPageSelected
                ? prev.filter((id) => !pageIds.includes(id))
                : [...new Set([...prev, ...pageIds])]
        ));
    };

    const toggleOne = (id) => {
        setSelected((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const url = editingId
            ? `${API_URL}/api/providers/${editingId}`
            : `${API_URL}/api/providers`;
        try {
            const res = await fetch(url, {
                method: editingId ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
                body: JSON.stringify(formData),
            });
            if (res.ok) {
                setModalOpen(false);
                setFormData(emptyForm);
                setEditingId(null);
                fetchData();
            } else {
                alert('Error al guardar proveedor');
            }
        } catch (err) {
            console.error(err);
        }
    };

    const deactivate = async (ids) => {
        const token = localStorage.getItem('token');
        await Promise.all(ids.map((id) => fetch(`${API_URL}/api/providers/${id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
        })));
        setSelected((prev) => prev.filter((id) => !ids.includes(id)));
        fetchData();
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(`¿Desactivar al proveedor "${name}"?`)) return;
        try {
            await deactivate([id]);
        } catch (err) {
            console.error(err);
        }
    };

    const deactivateSelected = async () => {
        if (selected.length === 0) return;
        if (!window.confirm(`¿Desactivar ${selected.length} proveedor${selected.length === 1 ? '' : 'es'}?`)) return;
        try {
            await deactivate(selected);
        } catch (err) {
            console.error(err);
        }
    };

    const openEdit = (provider) => {
        setFormData({
            nombre: provider.nombre || '',
            telefono: provider.telefono || '',
            email: provider.email || '',
            direccion: provider.direccion || '',
        });
        setEditingId(provider.proveedor_id);
        setModalOpen(true);
    };

    const openCreate = () => {
        setEditingId(null);
        setFormData(emptyForm);
        setModalOpen(true);
    };

    const exportCsv = () => {
        const header = ['ID', 'Nombre', 'Telefono', 'Email', 'Direccion'];
        const lines = filtered.map((provider) => [
            provider.proveedor_id,
            provider.nombre,
            provider.telefono,
            provider.email,
            provider.direccion,
        ].map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(','));
        const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'proveedores.csv';
        link.click();
        URL.revokeObjectURL(link.href);
    };

    return (
        <div className="users-screen">
            <div className="users-head">
                <div className="users-title">
                    <span className="users-title-icon" aria-hidden><Truck size={22} /></span>
                    <div>
                        <h1>Gestión de Proveedores</h1>
                        <p>Administra los datos de contacto de tus proveedores.</p>
                    </div>
                </div>
                <button type="button" className="users-new" onClick={openCreate}>
                    <Plus size={18} /> Nuevo Proveedor
                </button>
            </div>

            <div className="users-card">
                <div className="users-toolbar">
                    <label className="users-search">
                        <Search size={16} aria-hidden />
                        <input
                            value={query}
                            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                            placeholder="Buscar proveedores por nombre, email o teléfono..."
                        />
                    </label>
                    <label className="users-filter">
                        <ArrowUpDown size={15} aria-hidden />
                        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                            <option value="nombre">Nombre</option>
                            <option value="recientes">Más recientes</option>
                            <option value="telefono">Teléfono</option>
                        </select>
                    </label>
                    <button type="button" className="users-ghost" onClick={exportCsv}>
                        <Download size={16} /> Exportar
                    </button>
                    <button type="button" className="users-danger" onClick={deactivateSelected} disabled={selected.length === 0}>
                        <Trash2 size={15} /> Desactivar
                    </button>
                </div>

                {loading ? (
                    <p className="users-empty">Cargando proveedores...</p>
                ) : (
                    <div className="users-table-wrap">
                        <table className="users-table">
                            <thead>
                                <tr>
                                    <th>
                                        <input type="checkbox" checked={allPageSelected} onChange={toggleAllPage} aria-label="Seleccionar página" />
                                    </th>
                                    <th>Proveedor</th>
                                    <th>Teléfono</th>
                                    <th>Email</th>
                                    <th>Dirección</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pageRows.map((provider) => (
                                    <tr key={provider.proveedor_id}>
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={selected.includes(provider.proveedor_id)}
                                                onChange={() => toggleOne(provider.proveedor_id)}
                                                aria-label={`Seleccionar ${provider.nombre}`}
                                            />
                                        </td>
                                        <td>
                                            <div className="users-person">
                                                <span className="users-avatar" style={{ background: avatarColor(provider.nombre) }}>
                                                    {initialsOf(provider.nombre)}
                                                </span>
                                                <span>
                                                    <strong>{provider.nombre}</strong>
                                                    <small>ID: {provider.proveedor_id}</small>
                                                </span>
                                            </div>
                                        </td>
                                        <td>{provider.telefono || '—'}</td>
                                        <td className="users-email">{provider.email || '—'}</td>
                                        <td>{provider.direccion || '—'}</td>
                                        <td>
                                            <div className="users-actions">
                                                <button type="button" className="is-edit" title="Editar" onClick={() => openEdit(provider)}>
                                                    <Edit size={16} />
                                                </button>
                                                <button type="button" title="Desactivar" className="is-danger" onClick={() => handleDelete(provider.proveedor_id, provider.nombre)}>
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {pageRows.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="users-empty">No hay proveedores con esos filtros.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="users-footer">
                    <span>Mostrando {from} a {to} de {filtered.length} proveedores</span>
                    <div className="users-pager">
                        <label>
                            Filas por página:
                            <select
                                value={pageSize}
                                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                            >
                                {[10, 20, 50].map((size) => <option key={size} value={size}>{size}</option>)}
                            </select>
                        </label>
                        <button type="button" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={safePage === 1} aria-label="Página anterior">
                            <ChevronLeft size={16} />
                        </button>
                        {Array.from({ length: pageCount }, (_, index) => index + 1).slice(0, 6).map((n) => (
                            <button
                                key={n}
                                type="button"
                                className={n === safePage ? 'is-current' : ''}
                                onClick={() => setPage(n)}
                            >
                                {n}
                            </button>
                        ))}
                        <button type="button" onClick={() => setPage((current) => Math.min(pageCount, current + 1))} disabled={safePage === pageCount} aria-label="Página siguiente">
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            </div>

            {modalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-lg">
                        <h2 className="modal-title">{editingId ? 'Editar Proveedor' : 'Nuevo Proveedor'}</h2>
                        <form onSubmit={handleSubmit} className="form-grid">
                            <div className="form-group">
                                <label>Nombre</label>
                                <input required value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Teléfono</label>
                                <input value={formData.telefono} onChange={(e) => setFormData({ ...formData, telefono: e.target.value })} />
                            </div>
                            <div className="form-group full-width">
                                <label>Email</label>
                                <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                            </div>
                            <div className="form-group full-width">
                                <label>Dirección</label>
                                <input value={formData.direccion} onChange={(e) => setFormData({ ...formData, direccion: e.target.value })} />
                            </div>
                            <div className="form-actions">
                                <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded">Cancelar</button>
                                <button type="submit" className="btn-primary">Guardar</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
